import React from 'react';
import { Alert, Box, Button, LinearProgress, Slider, Stack, Typography } from '@mui/material';
import CropIcon from '@mui/icons-material/CropOutlined';
import FaceIcon from '@mui/icons-material/FaceRetouchingNatural';
import type { EditingAllowance } from '../../../services/editingApi';
import type { EditList, OutputAspect, OutputResolution, ReframeMode } from '../../../utils/editing/editList';
import { OUTPUT_ASPECTS, OUTPUT_RESOLUTIONS } from '../../../utils/editing/editList';
import { focusAt } from '../../../utils/editing/editList';
import { ASPECT_LABELS, RESOLUTION_LABELS } from '../editStudioModel';
import type { FollowPerson } from '../useFollowPerson';
import { Choice, PanelHint } from './controls';

interface OutputPanelProps {
  list: EditList;
  allowance: EditingAllowance | null;
  onChange: (next: EditList, key?: string) => void;
  /** Shows the whole picture with the crop window to drag. */
  positionCrop: boolean;
  onTogglePositionCrop: () => void;
  /** Finding the person and moving the crop with them. */
  follow?: FollowPerson;
  /** The source time the preview is at (for "stop following": keep the crop where it is now). */
  atMs?: number;
}

const RANK: Record<OutputResolution, number> = { '720p': 0, '1080p': 1, '2160p': 2 };

/** The result's frame: size, shape (a Short, a square) and what the reframe keeps. */
const OutputPanel: React.FC<OutputPanelProps> = ({ list, allowance, onChange, positionCrop, onTogglePositionCrop, follow, atMs = 0 }) => {
  const maxRank = allowance ? RANK[allowance.maxResolution] : 1;
  const reframe = list.reframe || { focusXPct: 50, focusYPct: 50, mode: 'crop' as ReframeMode, track: [] };
  const following = reframe.track.length > 0;
  const now = focusAt(reframe, atMs);
  const setAspect = (aspect: OutputAspect) => onChange({ ...list, output: { ...list.output, aspect }, reframe: aspect === 'source' ? null : { ...reframe } });
  return (
    <Box>
      <PanelHint>How the export is framed. The stage shows exactly what it keeps.</PanelHint>
      <Stack spacing={1.5}>
        <Choice<OutputAspect> label="Shape" value={list.output.aspect} options={OUTPUT_ASPECTS.map((aspect) => ({ value: aspect, label: ASPECT_LABELS[aspect] }))} onChange={setAspect} testId="output-aspect" />
        {list.output.aspect !== 'source' ? (
          <>
            <Choice<ReframeMode> label="Fit" value={reframe.mode} options={[{ value: 'crop', label: 'Fill the frame (crop the sides)' }, { value: 'pad', label: 'Fit the whole picture (bars)' }]} onChange={(mode) => onChange({ ...list, reframe: { ...reframe, mode } })} />
            {reframe.mode === 'crop' ? (
              <Box>
                <Button size="small" variant={positionCrop ? 'contained' : 'outlined'} startIcon={<CropIcon />} onClick={onTogglePositionCrop} data-testid="toggle-position-crop" sx={{ mb: 1 }}>
                  {positionCrop ? 'Done positioning' : 'Position the crop on the picture'}
                </Button>
                {following ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} data-testid="following-note">
                    Following the person: {reframe.track.length} {reframe.track.length === 1 ? 'position' : 'positions'}. Here the crop keeps {Math.round(now.focusXPct)}% across. Drag the crop on the picture to change this moment.
                  </Typography>
                ) : (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Keep in frame: {Math.round(reframe.focusXPct)}% across, {Math.round(reframe.focusYPct)}% down</Typography>
                    <Slider size="small" min={0} max={100} value={reframe.focusXPct} onChange={(_, v) => onChange({ ...list, reframe: { ...reframe, focusXPct: v as number } }, 'focus-x')} aria-label="Focus across" />
                    <Slider size="small" min={0} max={100} value={reframe.focusYPct} onChange={(_, v) => onChange({ ...list, reframe: { ...reframe, focusYPct: v as number } }, 'focus-y')} aria-label="Focus down" />
                  </>
                )}
                {follow ? (
                  <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Follow the person</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Finds the face through the parts you kept and moves the crop with it. With two people it stays on one until they leave the frame. Faces are found in your browser; your video isn’t sent anywhere for this.
                    </Typography>
                    {follow.progress ? (
                      <Box data-testid="follow-progress">
                        <LinearProgress variant="determinate" value={(follow.progress.done / Math.max(1, follow.progress.total)) * 100} sx={{ mb: 0.75 }} />
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary">Looking at frame {follow.progress.done} of {follow.progress.total}…</Typography>
                          <Button size="small" onClick={follow.cancel}>Stop</Button>
                        </Stack>
                      </Box>
                    ) : (
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button size="small" variant="outlined" startIcon={<FaceIcon />} onClick={follow.start} data-testid="follow-person">
                          {following ? 'Find the person again' : 'Follow the person'}
                        </Button>
                        {following ? (
                          <Button size="small" onClick={() => follow.stop(atMs)} data-testid="stop-following">Keep one position</Button>
                        ) : null}
                      </Stack>
                    )}
                    {follow.message ? (
                      <Alert severity={follow.message.tone === 'ok' ? 'success' : follow.message.tone === 'error' ? 'error' : 'info'} sx={{ mt: 1, py: 0, fontSize: 13 }} data-testid="follow-message">
                        {follow.message.text}
                      </Alert>
                    ) : null}
                  </Box>
                ) : null}
              </Box>
            ) : null}
          </>
        ) : null}
        <Choice<OutputResolution>
          label="Size"
          value={list.output.resolution}
          options={OUTPUT_RESOLUTIONS.map((resolution) => ({ value: resolution, label: RESOLUTION_LABELS[resolution] + (RANK[resolution] > maxRank ? ' · Pro Max' : ''), disabled: RANK[resolution] > maxRank }))}
          onChange={(resolution) => onChange({ ...list, output: { ...list.output, resolution } })}
          testId="output-resolution"
        />
        <Choice
          label="Frame rate"
          value={list.output.fps === null ? 'source' : String(list.output.fps)}
          options={[{ value: 'source', label: 'As recorded' }, { value: '24', label: '24' }, { value: '25', label: '25' }, { value: '30', label: '30' }, { value: '60', label: '60' }]}
          onChange={(value) => onChange({ ...list, output: { ...list.output, fps: value === 'source' ? null : Number(value) } })}
        />
      </Stack>
    </Box>
  );
};

export default OutputPanel;
